import { useState } from "react";

import {
  Clock,
  Database,
  Globe,
  Hash,
  Link,
  Loader2,
  Map,
  MapPin,
  RefreshCw,
  Users,
} from "lucide-react";

import {
  EnrichmentAsyncHint,
  shouldSuppressEnrichCta,
} from "@/components/common/enrichment-async-hint";
import { EnrichmentSummary } from "@/components/enrichment/enrichment-summary";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { GeodataViewType } from "@/pages/geodata/schemas/geodata.edit.form";
import { EntityTinaTrace } from "@/pages/tina/components/entity-tina-trace";

const typeStyles: Record<string, { bg: string; text: string }> = {
  CITY: { bg: "bg-chart-2/15", text: "text-chart-2" },
  ADM2: { bg: "bg-chart-1/15", text: "text-chart-1" },
  ISLAND: { bg: "bg-chart-3/15", text: "text-chart-3" },
};

/**
 * `type` is a closed `z.enum` at compile time and a bare string at runtime, and
 * the read is lenient now: a fourth geodata type fails `parseRead`, comes
 * back raw, and reaches this map as a miss. Dereferencing `undefined.bg` blanked
 * the whole page — a worse failure than the error toast the strict read gave.
 */
const UNKNOWN_TYPE_STYLE = { bg: "bg-muted", text: "text-muted-foreground" };

function formatPopulation(pop: number): string {
  return pop.toLocaleString("en-US");
}

function FieldRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
          {label}
        </span>
        <span className="text-sm text-card-foreground break-words">
          {value ?? <span className="text-muted-foreground italic">null</span>}
        </span>
      </div>
    </div>
  );
}

export function GeodataPreview({
  item,
  usedOn,
  usedOnVenues,
  onTriggerEnrich,
  justCreated = false,
}: {
  item: GeodataViewType;
  usedOn: number;
  usedOnVenues: number;
  onTriggerEnrich?: () => Promise<boolean> | void;
  /** `?created=1` off the post-create redirect. See `enrichment-async-hint`. */
  justCreated?: boolean;
}) {
  const style = typeStyles[item.type] ?? UNKNOWN_TYPE_STYLE;
  const [isEnriching, setIsEnriching] = useState(false);

  const enrichmentStatus = item.enrichmentStatus ?? "Unavailable";
  // Geodata's create was the LAST synchronous enrichment in the estate; it now
  // returns before the job has run, so the row this renders is legitimately
  // unenriched for its first seconds. Offering "Run enrichment" there is
  // harmless (it debounces) but teaches the wrong model.
  const canRetry =
    !!onTriggerEnrich &&
    !shouldSuppressEnrichCta(enrichmentStatus, justCreated) &&
    (enrichmentStatus === "Failed" ||
      enrichmentStatus === "Unavailable" ||
      enrichmentStatus === "Enriched" ||
      enrichmentStatus === "NeedsReview");

  const handleEnrich = async () => {
    if (!onTriggerEnrich) return;
    setIsEnriching(true);
    try {
      await onTriggerEnrich();
    } finally {
      setIsEnriching(false);
    }
  };

  return (
    <aside className="flex w-1/4 min-w-[260px] flex-col gap-4 border-l border-border bg-card p-5 rounded-l-2xl max-h-[calc(100vh-48px)] sticky top-0">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
          Preview
        </p>
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-card-foreground tracking-tight truncate">
            {item.name}
          </h2>
          <Badge
            variant="secondary"
            className={`${style.bg} ${style.text} text-[11px] font-semibold px-2 py-0.5 shrink-0 border-0`}
          >
            {item.type}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {item.country} ({item.countryCode})
        </p>
      </div>

      {/* Fields */}
      <div className="flex-1 overflow-y-auto px-5 py-2">
        <FieldRow icon={Hash} label="ID" value={item.id} />
        <Separator />
        <FieldRow icon={MapPin} label="Name" value={item.name} />
        <Separator />
        <FieldRow
          icon={Globe}
          label="Country"
          value={`${item.country} (${item.countryCode})`}
        />
        <Separator />
        <FieldRow
          icon={Map}
          label="Region"
          value={
            <span>
              {item.region}
              <span className="text-muted-foreground text-xs ml-1.5">
                {item.regionCode}
              </span>
            </span>
          }
        />
        <Separator />
        <FieldRow icon={Clock} label="Timezone" value={item.timezone} />
        <Separator />
        {item.population && (
          <FieldRow
            icon={Users}
            label="Population"
            value={formatPopulation(item.population)}
          />
        )}
        <Separator />
        <FieldRow icon={Link} label="WikiData ID" value={item.wikiDataId} />
        <Separator />
        <FieldRow icon={Link} label="Region WD ID" value={item.regionWdId} />
        <Separator />
        <FieldRow icon={Database} label="GeoDB ID" value={item.geoDbId} />
        <Separator />
        {item.moeMapping && (
          <div className="flex items-start gap-3 py-2">
            <Database className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                MOE Mapping
              </span>
              <div className="flex flex-wrap gap-1.5">
                {item.moeMapping.map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="text-[11px] font-normal px-2 py-0.5"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}
        <Separator />
        <FieldRow icon={Users} label="Used On Events" value={usedOn} />
        <Separator />
        <FieldRow icon={Users} label="Used On Venues" value={usedOnVenues} />
        <Separator />
        <div className="flex flex-col gap-3 py-3">
          <EnrichmentSummary
            resource="geodata"
            id={item.id}
            status={enrichmentStatus}
            enrichment={item.enrichment}
          />
          <EnrichmentAsyncHint
            status={enrichmentStatus}
            justCreated={justCreated}
          />
          {canRetry && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs gap-1 self-start"
              onClick={handleEnrich}
              disabled={isEnriching}
            >
              {isEnriching ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              {enrichmentStatus === "Failed" ||
              enrichmentStatus === "Unavailable"
                ? "Run enrichment"
                : "Re-run"}
            </Button>
          )}
        </div>
        {/* Which Tina job last enriched this geodata row. Geodata is ungated —
            it has no confidence gate and can never park — so this is provenance
            only, never a review affordance. */}
        <EntityTinaTrace
          entityType="geodata"
          entityId={item.id}
          metadata={item.metadata}
          enrichment={item.enrichment}
        />
      </div>
    </aside>
  );
}
